/*
  assets/kw-lucky-drop.js - the "Lucky drop" pop-up (sections/kw-lucky-drop.liquid).

  Flow: opens once, after a delay, for a new visitor -> the ball drops through the peg board and lands on
  a prize (picked by the slots' weights first; the bounces are then chosen so the ball ends in that slot)
  -> email through the theme's customer form, tagged with the prize -> "your code is on its way".
  The code itself is never shown on screen: stamp-pipeline/src/lucky-drop.mjs makes a unique single-use
  code for that email and sends it.

  State lives in localStorage "kw.lucky": {status: shown|skipped|played|claimed, prize, at}. It is written the
  moment the pop-up opens, so a closed tab counts as seen: the pop-up never comes back unless the section's
  "Show again after" setting is above 0 days, and never after a claim. Only a share of new visitors is
  shown the pop-up at all (section setting "Show to", 20% by default); the rest are remembered as "skipped".

  The customer form is submitted for real (not with fetch): the store's spam protection needs the CAPTCHA
  token that Shopify's own script adds to a native submission. Shopify then sends the visitor back with
  ?customer_posted=true and the "sent" step is shown.
*/
(function () {
  'use strict';

  const KEY = 'kw.lucky';
  const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function readState() {
    try {
      return JSON.parse(localStorage.getItem(KEY) || 'null') || {};
    } catch (e) {
      return {};
    }
  }

  function writeState(state) {
    try {
      localStorage.setItem(KEY, JSON.stringify(state));
    } catch (e) {
      /* private mode: the pop-up simply behaves like a first visit next time */
    }
  }

  const easeOut = (t) => 1 - Math.pow(1 - t, 2);
  const easeIn = (t) => t * t;

  /* ---------- The board ---------- */
  class Board {
    constructor(canvas, prizes) {
      this.canvas = canvas;
      this.ctx = canvas.getContext('2d');
      this.prizes = prizes;
      this.rows = prizes.length - 1; // R rows of pegs give R + 1 slots
      this.ball = null;
      this.winner = -1;
      this.font = getComputedStyle(document.body).fontFamily || 'sans-serif';
      this.fg = getComputedStyle(canvas).color || '#103948';
      this.bg = getComputedStyle(canvas.closest('.kw-lucky__card') || canvas).backgroundColor || '#fff';
      this.layout();
    }

    layout() {
      const w = this.canvas.clientWidth || 300;
      this.w = w;
      this.h = Math.round(w * 0.98);
      const dpr = Math.min(window.devicePixelRatio || 1, 3);
      this.canvas.width = Math.round(this.w * dpr);
      this.canvas.height = Math.round(this.h * dpr);
      this.canvas.style.height = this.h + 'px';
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      this.gap = this.w / (this.rows + 2);
      this.cx = this.w / 2;
      this.slotH = 44;
      this.pegR = Math.max(2.5, this.gap * 0.075);
      this.ballR = Math.max(6, this.gap * 0.19);
      this.pegTop = 30;
      this.pegBottom = this.h - this.slotH - 22;
      this.rowGap = this.rows > 1 ? (this.pegBottom - this.pegTop) / (this.rows - 1) : 0;
      this.draw();
    }

    // Peg i of row r (row r has r + 3 pegs: one spare on each side acts as the wall)
    peg(r, i) {
      return { x: this.cx + (2 * i - r - 2) * (this.gap / 2), y: this.pegTop + r * this.rowGap };
    }

    // Where the ball rests on the peg it meets in row r after k bounces to the right
    rest(r, k) {
      return { x: this.cx + (2 * k - r) * (this.gap / 2), y: this.pegTop + r * this.rowGap - this.pegR - this.ballR + 1 };
    }

    slotX(k) {
      return this.cx + (2 * k - this.rows) * (this.gap / 2);
    }

    draw() {
      const ctx = this.ctx;
      const { w, h, gap, slotH } = this;
      ctx.clearRect(0, 0, w, h);

      // pegs
      ctx.fillStyle = this.fg;
      for (let r = 0; r < this.rows; r++) {
        for (let i = 0; i < r + 3; i++) {
          const p = this.peg(r, i);
          ctx.globalAlpha = i === 0 || i === r + 2 ? 0.25 : 0.55;
          ctx.beginPath();
          ctx.arc(p.x, p.y, this.pegR, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.globalAlpha = 1;

      // slots
      const top = h - slotH - 4;
      ctx.lineWidth = 1;
      ctx.strokeStyle = this.fg;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      if ('letterSpacing' in ctx) ctx.letterSpacing = '0.5px';
      for (let k = 0; k <= this.rows; k++) {
        const x = this.slotX(k);
        const left = x - gap / 2;
        const win = k === this.winner;
        if (win) {
          ctx.fillStyle = this.fg;
          ctx.fillRect(left, top, gap, slotH);
        }
        ctx.globalAlpha = 0.6;
        ctx.beginPath();
        ctx.moveTo(left + 0.5, top);
        ctx.lineTo(left + 0.5, h - 4);
        if (k === this.rows) {
          ctx.moveTo(left + gap - 0.5, top);
          ctx.lineTo(left + gap - 0.5, h - 4);
        }
        ctx.stroke();
        ctx.globalAlpha = 1;
        ctx.fillStyle = win ? this.bg : this.fg;
        const lines = String(this.prizes[k].short || '').split(/\\n|\n/);
        const size = lines.length > 1 ? 9 : lines[0].length > 3 ? 9 : 11;
        ctx.font = '500 ' + size + 'px ' + this.font;
        const lh = size + 2;
        const y0 = top + slotH / 2 + 1 - ((lines.length - 1) * lh) / 2;
        lines.forEach((line, i) => ctx.fillText(line.trim(), x, y0 + i * lh, gap - 4));
      }
      ctx.globalAlpha = 0.6;
      ctx.beginPath();
      ctx.moveTo(this.slotX(0) - gap / 2, h - 3.5);
      ctx.lineTo(this.slotX(this.rows) + gap / 2, h - 3.5);
      ctx.stroke();
      ctx.globalAlpha = 1;

      // ball
      if (this.ball) {
        ctx.fillStyle = this.fg;
        ctx.beginPath();
        ctx.arc(this.ball.x, this.ball.y, this.ballR, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = this.bg;
        ctx.globalAlpha = 0.35;
        ctx.beginPath();
        ctx.arc(this.ball.x - this.ballR * 0.3, this.ball.y - this.ballR * 0.3, this.ballR * 0.3, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;
      }
    }

    // Drops the ball so that it ends in slot `target`; resolves when it has settled.
    drop(target) {
      const R = this.rows;
      const bounces = [];
      for (let i = 0; i < R; i++) bounces.push(i < target ? 1 : 0);
      for (let i = bounces.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [bounces[i], bounces[j]] = [bounces[j], bounces[i]];
      }

      const points = [{ x: this.cx, y: -this.ballR }];
      let k = 0;
      for (let r = 0; r < R; r++) {
        points.push(this.rest(r, k));
        k += bounces[r];
      }
      const settle = { x: this.slotX(k), y: this.h - this.slotH / 2 - 4 };
      points.push(settle);

      this.winner = -1;
      if (reduced) {
        this.ball = settle;
        this.winner = k;
        this.draw();
        return Promise.resolve(k);
      }

      return new Promise((resolve) => {
        let seg = 0;
        let segStart = null;
        const durations = points.map((p, i) => (i === 0 ? 0 : i === 1 ? 260 : i === points.length - 1 ? 300 : 230));
        const step = (now) => {
          if (segStart === null) segStart = now;
          const from = points[seg];
          const to = points[seg + 1];
          const t = Math.min(1, (now - segStart) / durations[seg + 1]);
          const last = seg + 1 === points.length - 1;
          const x = from.x + (to.x - from.x) * easeOut(t);
          let y = from.y + (to.y - from.y) * easeIn(t);
          if (seg > 0 && !last) y -= Math.sin(Math.PI * t) * this.rowGap * 0.18;
          if (last) y = from.y + (to.y - from.y) * (t < 0.7 ? easeIn(t / 0.7) : 1 - Math.sin(((t - 0.7) / 0.3) * Math.PI) * 0.06);
          this.ball = { x, y };
          this.draw();
          if (t >= 1) {
            seg++;
            segStart = null;
            if (seg + 1 >= points.length) {
              this.ball = settle;
              this.winner = k;
              this.draw();
              resolve(k);
              return;
            }
          }
          requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      });
    }
  }

  /* ---------- The pop-up ---------- */
  class KwLuckyDrop extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      try {
        this.prizes = JSON.parse(this.querySelector('[data-kw-prizes]').textContent);
      } catch (e) {
        this.prizes = [];
      }
      if (this.prizes.length < 3) return;

      this.design = !!(window.Shopify && window.Shopify.designMode);
      this.card = this.querySelector('.kw-lucky__card');
      this.heading = this.querySelector('[data-kw-heading]');
      this.boardEl = this.querySelector('[data-kw-board]');
      this.canvas = this.querySelector('canvas');
      this.form = this.querySelector('form');
      this.tagsInput = this.form.querySelector('input[name="contact[tags]"]');
      this.error = this.querySelector('[data-kw-error]');
      this.steps = {};
      this.querySelectorAll('[data-kw-step]').forEach((el) => (this.steps[el.dataset.kwStep] = el));
      this.state = readState();
      this.busy = false;
      this.playHeading = this.heading.textContent.trim();

      this.querySelectorAll('[data-kw-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      this.querySelector('[data-kw-drop]').addEventListener('click', () => this.drop());
      this.boardEl.addEventListener('click', () => this.drop());
      this.form.addEventListener('submit', (e) => this.submit(e));
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });

      this.board = new Board(this.canvas, this.prizes);
      if ('ResizeObserver' in window) {
        new ResizeObserver(() => {
          if (this.classList.contains('is-open') && !this.busy) this.board.layout();
        }).observe(this.canvas);
      }

      if (this.design) {
        document.addEventListener('shopify:section:select', (e) => {
          if (e.detail.sectionId === this.dataset.section) this.open('play');
        });
        document.addEventListener('shopify:section:deselect', (e) => {
          if (e.detail.sectionId === this.dataset.section) this.close(true);
        });
        return;
      }

      this.decide();
    }

    decide() {
      const s = this.state;
      const params = new URLSearchParams(location.search);

      // Arrived from the prize email (?lucky=off): they have their code, never show the game here
      if (params.get('lucky') === 'off') {
        if (s.status !== 'claimed') {
          this.state = { status: 'claimed', prize: s.prize || null, at: Date.now() };
          writeState(this.state);
        }
        return;
      }

      // Back from Shopify after the email was accepted
      if (params.get('customer_posted') === 'true' && s.status === 'played' && s.pendingClaim) {
        this.state = { status: 'claimed', prize: s.prize, at: Date.now() };
        writeState(this.state);
        this.open('sent');
        return;
      }

      if (s.status === 'claimed') return;
      if (s.status && s.at) {
        const again = Number(this.dataset.showAgainDays || 0);
        if (again <= 0 || Date.now() - s.at < again * 864e5) return;
      }

      // Paid visitors see the page they clicked through to: no auto-open for the rest of that visit.
      if (this.fromAd(params)) return;

      // Only a share of visitors gets the game (section setting "Show to"). The draw is made once and
      // remembered like a viewing, so the others are not drawn again on every page.
      const chance = Number(this.dataset.showChance);
      if (chance < 100 && Math.random() * 100 >= chance) {
        this.state = { status: 'skipped', at: Date.now() };
        writeState(this.state);
        return;
      }

      setTimeout(() => this.whenBannerGone().then(() => this.open('play')), Number(this.dataset.delay || 0) * 1000);
    }

    // An ad click carries a click id (Google Ads auto-tagging, Microsoft Ads) or a paid utm_medium; remembered per tab session.
    fromAd(params) {
      const paid = ['gclid', 'gbraid', 'wbraid', 'msclkid'].some((k) => params.has(k)) ||
        /^(cpc|ppc|paid|paid[-_ ]?search|paid[-_ ]?social)$/i.test(params.get('utm_medium') || '');
      try {
        if (paid) sessionStorage.setItem('kw.adVisit', '1');
        return paid || sessionStorage.getItem('kw.adVisit') === '1';
      } catch (e) {
        return paid;
      }
    }

    // Shopify's cookie-consent banner sits above everything; let the visitor answer it first.
    whenBannerGone() {
      const visible = () => {
        const b = document.getElementById('shopify-pc__banner');
        return b && b.offsetParent !== null && getComputedStyle(b).visibility !== 'hidden';
      };
      if (!visible()) return Promise.resolve();
      return new Promise((resolve) => {
        const timer = setInterval(() => {
          if (!visible()) {
            clearInterval(timer);
            resolve();
          }
        }, 400);
      });
    }

    open(step) {
      this.show(step);
      this.classList.add('is-open');
      this.board.layout();
      this.card.focus({ preventScroll: true });
      if (!this.design && step === 'play') {
        // Seen once is enough: remember it now, so a closed tab counts too
        this.state = { status: 'shown', at: Date.now() };
        writeState(this.state);
      }
    }

    close(silent) {
      if (this.busy) return;
      this.classList.remove('is-open');
      if (silent || this.design) return;
      if (!this.state.at) {
        this.state = { status: 'shown', at: Date.now() };
        writeState(this.state);
      }
    }

    show(step) {
      Object.keys(this.steps).forEach((k) => (this.steps[k].hidden = k !== step));
      if (step === 'play') {
        this.heading.textContent = this.playHeading;
      } else if (step === 'claim') {
        this.heading.textContent = this.dataset.wonHeading.replace('[prize]', this.state.prize.title);
      } else if (step === 'sent') {
        this.heading.textContent = this.dataset.sentHeading;
      }
    }

    pick() {
      const total = this.prizes.reduce((sum, p) => sum + Math.max(0, Number(p.weight) || 0), 0);
      if (total <= 0) return Math.floor(Math.random() * this.prizes.length);
      let r = Math.random() * total;
      for (let i = 0; i < this.prizes.length; i++) {
        r -= Math.max(0, Number(this.prizes[i].weight) || 0);
        if (r < 0) return i;
      }
      return this.prizes.length - 1;
    }

    async drop() {
      if (this.busy || this.steps.play.hidden) return;
      this.busy = true;
      this.boardEl.classList.add('is-busy');
      const target = this.pick();
      const slot = await this.board.drop(target);
      this.boardEl.classList.remove('is-busy');
      this.boardEl.classList.add('is-done');
      this.state = { status: 'played', prize: this.prizes[slot] || this.prizes[target], at: Date.now() };
      if (!this.design) writeState(this.state);
      await new Promise((r) => setTimeout(r, reduced ? 300 : 900));
      this.busy = false;
      this.show('claim');
      const input = this.form.querySelector('input[type="email"]');
      if (input) input.focus({ preventScroll: true });
    }

    submit(e) {
      if (this.native) return; // the real submission, let it through
      e.preventDefault();
      const input = this.form.querySelector('input[type="email"]');
      const email = (input.value || '').trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this.fail();
        return;
      }
      this.error.hidden = true;

      if (this.design) {
        this.show('sent');
        return;
      }

      // The prize travels as a customer tag; stamp-pipeline reads it and emails a unique code
      const key = String((this.state.prize && this.state.prize.key) || '').replace(/[^a-z0-9-]/gi, '');
      this.tagsInput.value = 'newsletter,lucky-drop' + (key ? ',lucky-prize-' + key : '');
      this.state = Object.assign({}, this.state, { status: 'played', pendingClaim: true });
      writeState(this.state);
      this.busy = true;
      this.native = true;
      this.form.requestSubmit();
      this.form.querySelector('button[type="submit"]').disabled = true;
    }

    fail() {
      this.error.textContent = this.dataset.emailError;
      this.error.hidden = false;
    }
  }

  if (!customElements.get('kw-lucky-drop')) customElements.define('kw-lucky-drop', KwLuckyDrop);
})();
